/**
 * **Metode bayar** — dan ini **pemblokir keras onboarding** (Imron, 2026-08-04).
 *
 * ### ⛔⛔ Kenapa layar ini paling mendesak dari semua yang belum ada
 * Gerbang kesiapan outlet menuntut minimal satu metode bayar ber-`tender_type`. Tanpa layar ini,
 * tenant yang baru di-onboard bisa menata seluruh menu, harga, pajak, dan outletnya — lalu
 * **mentok**, dan yang menahannya bukan kesalahan dia. Satu-satunya jalan keluar sebelumnya adalah
 * SQL langsung.
 *
 * ### ⛔⛔⛔ `tender_type` adalah KLASIFIKASI EKONOMI, bukan label
 * Inilah pelajaran termahal dari bedah VTEC: di sana **satu boolean** (`asItemDiscount`) memikul
 * tiga kategori ekonomi sekaligus, dan akibatnya beban Rp12,9jt, liabilitas Rp27,9M, serta piutang
 * Rp9,5jt tercampur jadi satu angka yang tak bisa dipisahkan lagi.
 *
 * ⇒ Di sini ia **enum lima nilai**, wajib dijawab, dan tiap pilihan menjelaskan **akibat
 * akuntansinya** — bukan sekadar namanya. Yang memilih adalah manajer, bukan akuntan; kalau
 * layarnya tak menjelaskan, yang dipilih adalah yang paling atas.
 *
 * ⚠️ ERP yang memetakan `tender_type` ke akun (`pos_tender_map`), bukan POS. POS cukup menyatakan
 * **jenis ekonominya** dengan benar — dan "dengan benar" itulah yang layar ini jaga.
 */
import { Alert, Form, Input, InputNumber, Select, Space, Switch, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { Brand, PaymentMethod } from '../api/client';
import { canWrite } from '../api/session';
import { CrudPanel } from '../components/CrudPanel';

/**
 * ⛔ Diambil dari KONTRAK lewat kunci i18n, bukan daftar yang disalin ke sini. Daftar salinan menua
 * diam-diam: nilai keenam yang ditambahkan di `packages/contract` takkan pernah muncul di layar,
 * dan tak ada yang memerah.
 */
const TENDER_TYPES = ['cash', 'clearing', 'liability_release', 'receivable', 'non_tender'] as const;

export function Payments() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [brand, setBrand] = useState<readonly Brand[]>([]);
  useEffect(() => { void api.brands(true).then(setBrand).catch(() => undefined); }, []);
  const namaBrand = (id: string) => brand.find((b) => b.id === id)?.code ?? id;

  const kolom: ColumnsType<PaymentMethod> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 140,
      render: (v: string) => <b data-testid={`bayar-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      /* ⭐ Lingkupnya tampil DI TABEL: "berlaku di mana" adalah hal pertama yang ditanyakan
         saat metode bayar tak muncul di kasir, dan menyembunyikannya di laci membuat orang
         memeriksa kapabilitas outlet lebih dulu — tempat yang salah. */
      title: t('payments.scope'),
      dataIndex: 'brand_id',
      width: 170,
      render: (v: string | null) =>
        v === null || v === undefined
          ? <Tag color="blue" data-testid="bayar-semua-brand">{t('payments.allBrands')}</Tag>
          : <Tag data-testid={`bayar-brand-${v}`}>{namaBrand(v)}</Tag>,
    },
    {
      /* ⭐ Jenis ekonomi tampil DI TABEL, bukan cuma di dalam laci: ia satu-satunya kolom yang
         salahnya tak terlihat sampai tutup buku. */
      title: t('payments.tenderType'),
      dataIndex: 'tender_type',
      width: 190,
      render: (v: string) => (
        <Tag color={v === 'cash' ? 'green' : v === 'receivable' ? 'red' : 'blue'}
             data-testid={`bayar-jenis-${v}`}>
          {t(`payments.tender.${v}`)}
        </Tag>
      ),
    },
    {
      title: t('payments.opensDrawer'),
      dataIndex: 'opens_drawer',
      width: 110,
      render: (v: boolean) => (v ? <Tag color="gold">{t('common.on')}</Tag> : <Tag>{t('common.off')}</Tag>),
    },
    {
      title: '',
      dataIndex: 'is_active',
      width: 96,
      render: (v: boolean) =>
        v ? <Tag color="green">{t('outlets.active')}</Tag> : <Tag>{t('outlets.inactive')}</Tag>,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="payments">
      {/* ⛔ Disebut di depan: outlet tanpa metode bayar TAK BISA berjualan sama sekali. */}
      <Alert type="warning" showIcon title={t('payments.required')} data-testid="bayar-wajib" />

      <CrudPanel<PaymentMethod>
        testId="bayar"
        historyPath="payment-methods"
        title={t('payments.title')}
        subtitle={t('payments.subtitle')}
        canWrite={boleh}
        newLabel={t('payments.new')}
        columns={kolom}
        fetchList={() => api.paymentMethods(true)}
        searchIn={(m) => `${m.code} ${m.name}`}
        emptyText={t('payments.empty')}
        renderFields={(_f, editing) => (
          <>
            {editing === null ? (
              <>
                <Form.Item
                  name="code"
                  label={t('master.code')}
                  rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                  tooltip={t('structure.codeFixed')}
                >
                  <Input maxLength={40} data-testid="bayar-kode" />
                </Form.Item>

                {/*
                  ⛔⛔ Jenis ekonomi WAJIB dijawab saat dibuat, dan TAK BISA diubah sesudahnya —
                  mengubahnya berarti transaksi kemarin berpindah kategori tanpa ada yang
                  memutuskannya, dan laporan lama ikut berubah bentuk.
                */}
                <Form.Item
                  name="tenderType"
                  label={t('payments.tenderType')}
                  rules={[{ required: true }]}
                  tooltip={t('payments.tenderHint')}
                >
                  <Select
                    data-testid="bayar-jenis"
                    options={TENDER_TYPES.map((v) => ({
                      value: v,
                      label: t(`payments.tender.${v}`),
                      /* ⭐ Akibat akuntansinya ikut di daftar pilihan — yang memilih manajer,
                         bukan akuntan. */
                      title: t(`payments.tenderWhy.${v}`),
                    }))}
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  {(f) => {
                    const v = String(f.getFieldValue('tenderType') ?? '');
                    return v === '' ? null : (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        data-testid="bayar-jenis-alasan"
                        title={t(`payments.tenderWhy.${v}`)}
                      />
                    );
                  }}
                </Form.Item>
              </>
            ) : (
              <Alert type="info" showIcon style={{ marginBottom: 16 }}
                     title={t('payments.typeLocked')} data-testid="bayar-jenis-terkunci" />
            )}

            <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
              <Input maxLength={120} data-testid="bayar-nama" />
            </Form.Item>

            <Space align="start" wrap>
              {/*
                ⭐ Laci = keputusan METODE BAYAR, bukan sifat printer. QRIS tak membukanya; laci
                yang terbuka tanpa uang masuk adalah laci yang tak lagi dijaga siapa pun.
              */}
              <Form.Item name="opensDrawer" label={t('payments.opensDrawer')} valuePropName="checked"
                         tooltip={t('payments.drawerHint')}>
                {/* ⚠️ `Switch` polos, bukan `OnOff`: di dalam `Form.Item` nilainya disuntik AntD,
                    dan komponen yang menuntut `checked`/`onChange` sendiri membuat siapa yang
                    menang jadi tak jelas — pola yang sudah pernah menipu di layar preset. */}
                {/*
                  ⚠️ `bayar-buka-laci`, BUKAN `bayar-laci`: `CrudPanel` sudah memakai
                  `<testId>-laci` untuk laci UI-nya sendiri. Dua arti "laci" dalam satu layar —
                  laci kas dan laci antarmuka — dan yang menemukan tabrakannya uji, bukan mata.
                */}
                <Switch data-testid="bayar-buka-laci"
                        checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
              </Form.Item>
              {/* ⚠️ Referensi wajib ⇒ kasir tak bisa menutup tanpa nomor approval/EDC. */}
              <Form.Item name="requiresReference" label={t('payments.requiresReference')}
                         valuePropName="checked" tooltip={t('payments.referenceHint')}>
                <Switch data-testid="bayar-referensi"
                        checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
              </Form.Item>
              {/*
                ⭐⭐ **Lewat mesin gesek (EDC)** ⇒ kasir WAJIB menyalin kode approval dari slip (K7).
                ⛔ Tak bisa ditebak dari jenis tender (kartu & QRIS sama-sama `clearing`) maupun
                dari nama metode. Ia dinyatakan di sini, sekali, oleh orang yang tahu mesinnya.
              */}
              <Form.Item name="viaEdc" label={t('payments.viaEdc')}
                         valuePropName="checked" tooltip={t('payments.viaEdcHint')}>
                <Switch data-testid="bayar-edc"
                        checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
              </Form.Item>
            </Space>

            <Space align="start" wrap>
              <Form.Item name="settlementProvider" label={t('payments.provider')}
                         tooltip={t('payments.providerHint')}>
                <Input maxLength={60} data-testid="bayar-provider" />
              </Form.Item>
              {/*
                ⛔ MDR ≠ diskon. Ia biaya bank atas pendapatan PENUH — mencatatnya sebagai potongan
                penjualan membuat omzet terlihat lebih kecil dan biayanya lenyap.
              */}
              <Form.Item name="mdrPercent" label={t('payments.mdr')} tooltip={t('payments.mdrHint')}>
                <InputNumber min={0} max={100} step={0.01} data-testid="bayar-mdr" />
              </Form.Item>
            </Space>

            {editing !== null && (
              <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked"
                         style={{ marginTop: 8 }}>
                <Switch
                  data-testid="bayar-aktif"
                  checkedChildren={t('outlets.active')}
                  unCheckedChildren={t('outlets.inactive')}
                />
              </Form.Item>
            )}
            {/*
              ⭐⭐ Lingkup brand — OPSIONAL. Kosong = berlaku di semua brand, dan itulah yang benar
              untuk CASH/QRIS. ⛔ Kalau ia diwajibkan, tenant dua-brand membuat `CASH` dua kali,
              dan dua baris CASH ber-id berbeda membuat laporan tender lintas-brand harus
              dijumlahkan sendiri — sementara pemetaan ke akun ERP jadi dua baris untuk SATU akun kas.
            */}
            <Form.Item name="brandId" label={t('payments.scope')} tooltip={t('payments.scopeHint')}>
              <Select
                allowClear
                placeholder={t('payments.allBrands')}
                data-testid="bayar-brand"
                options={brand.map((b) => ({ value: b.id, label: `${b.code} — ${b.name}` }))}
              />
            </Form.Item>
            <Typography.Text type="secondary">{t('payments.erpNote')}</Typography.Text>
          </>
        )}
        toFormValues={(m) => ({
          name: m.name,
          brandId: m.brand_id ?? null,
          opensDrawer: m.opens_drawer,
          requiresReference: m.requires_reference,
          viaEdc: (m as unknown as { via_edc?: boolean }).via_edc ?? false,
          settlementProvider: m.settlement_provider ?? '',
          /* ⚠️ Disimpan PECAHAN berskala 6, ditampilkan PERSEN — orang berpikir dalam persen. */
          mdrPercent: m.mdr_percent === null || m.mdr_percent === undefined
            ? undefined : Number(m.mdr_percent) * 100,
          isActive: m.is_active,
        })}
        onCreate={async (v) => {
          await api.createMaster('payment-methods', {
            code: String(v['code']).toUpperCase(),
            name: String(v['name']),
            tenderType: String(v['tenderType']),
            /* ⛔ `null` EKSPLISIT = semua brand. Menghilangkannya dari badan permintaan berarti
               "jangan diubah", dan itu arti yang berbeda. */
            brandId: v['brandId'] ?? null,
            opensDrawer: v['opensDrawer'] === true,
            requiresReference: v['requiresReference'] === true,
            ...(String(v['settlementProvider'] ?? '') === ''
              ? {} : { settlementProvider: String(v['settlementProvider']) }),
            ...(v['mdrPercent'] === undefined || v['mdrPercent'] === null
              ? {} : { mdrPercent: (Number(v['mdrPercent']) / 100).toFixed(6) }),
          });
        }}
        onUpdate={async (id, v, lama) => {
          await api.updateMaster('payment-methods', id, {
            name: v['name'],
            brandId: v['brandId'] ?? null,
            opensDrawer: v['opensDrawer'] === true,
            requiresReference: v['requiresReference'] === true,
            settlementProvider: String(v['settlementProvider'] ?? '') === ''
              ? null : v['settlementProvider'],
            mdrPercent: v['mdrPercent'] === undefined || v['mdrPercent'] === null
              ? null : (Number(v['mdrPercent']) / 100).toFixed(6),
          });
          if (v['isActive'] !== lama.is_active) {
            await api.setStatus('payment-methods', id, v['isActive'] === true);
          }
        }}
      />
    </Space>
  );
}
